import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  Link,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import {
  Check as CheckIcon,
  Delete as DeleteIcon,
  OpenInNew as OpenInNewIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useIssues, useUpdateIssueState, useDeleteIssue, ScraperIssue } from '../hooks/useIssues';
import { useMarketplaceDefinitions } from '../hooks/useMarketplaceDefinitions';
import { useProviderDisplayNames } from '../hooks/useProviderDisplayNames';
import { lookupUserDisplayName } from '../utils/userDisplay';

const Issues: React.FC = () => {
  const [stateFilter, setStateFilter] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [issueToDelete, setIssueToDelete] = useState<ScraperIssue | null>(null);

  const { data, isLoading, refetch } = useIssues(stateFilter, page, pageSize);
  const { data: definitions = [] } = useMarketplaceDefinitions();
  const updateStateMutation = useUpdateIssueState();
  const deleteMutation = useDeleteIssue();

  const issues = data?.items || [];
  const totalCount = data?.total || 0;

  const reporterProviderIds = useMemo(
    () => issues.map(i => i.provider_id).filter(Boolean) as string[],
    [issues]
  );
  const { displayNames: reporterNames } = useProviderDisplayNames(reporterProviderIds);

  // Create a map of marketplace IDs to names for display
  const marketplaceMap = useMemo(() => {
    const map: Record<string, string> = {};
    definitions.forEach((def: any) => {
      map[def.id] = def.name;
    });
    return map;
  }, [definitions]);

  const handleMarkFixed = async (issue: ScraperIssue) => {
    await updateStateMutation.mutateAsync({ id: issue.id, state: 'FIXED' });
  };

  const handleDeleteClick = (issue: ScraperIssue) => {
    setIssueToDelete(issue);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (issueToDelete) {
      await deleteMutation.mutateAsync(issueToDelete.id);
      setDeleteDialogOpen(false);
      setIssueToDelete(null);
    }
  };

  const getStateColor = (
    state: string
  ): 'default' | 'success' | 'warning' | 'error' | 'primary' => {
    switch (state) {
      case 'NEW':
        return 'error';
      case 'IN_PROGRESS':
        return 'warning';
      case 'FIXED':
        return 'success';
      case 'WONT_FIX':
        return 'default';
      default:
        return 'default';
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'created_at',
      headerName: 'Submitted',
      width: 150,
      renderCell: params => {
        const date = new Date(params.value);
        return (
          <Tooltip title={date.toLocaleString()}>
            <Typography variant="body2">{date.toLocaleDateString()}</Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'state',
      headerName: 'State',
      width: 120,
      renderCell: params => (
        <Chip label={params.value} color={getStateColor(params.value)} size="small" />
      ),
    },
    {
      field: 'occurrence_count',
      headerName: 'Count',
      width: 80,
      type: 'number',
      renderCell: params => {
        const count = params.value ?? 1;
        return <Typography variant="body2">{count > 1 ? count : '—'}</Typography>;
      },
    },
    {
      field: 'source',
      headerName: 'Source',
      width: 100,
      renderCell: params => (
        <Typography variant="body2" color="text.secondary">
          {params.value === 'scraper'
            ? 'Scraper'
            : params.value === 'extension'
              ? 'Extension'
              : '—'}
        </Typography>
      ),
    },
    {
      field: 'url',
      headerName: 'URL',
      flex: 1,
      minWidth: 250,
      renderCell: params => (
        <Link
          href={params.value}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {params.value}
          </Typography>
          <OpenInNewIcon sx={{ fontSize: 14, flexShrink: 0 }} />
        </Link>
      ),
    },
    {
      field: 'comment',
      headerName: 'Issue Description',
      flex: 1,
      minWidth: 200,
      renderCell: params => (
        <Tooltip title={params.value}>
          <Typography
            variant="body2"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {params.value}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'marketplace_id',
      headerName: 'Marketplace',
      width: 150,
      renderCell: params => {
        const name = params.value ? marketplaceMap[params.value] || params.value : 'Unknown';
        return (
          <Typography variant="body2" color="text.secondary">
            {name}
          </Typography>
        );
      },
    },
    {
      field: 'provider_id',
      headerName: 'Reporter',
      width: 120,
      renderCell: params => {
        const providerId = params.value as string | null;
        const displayName = lookupUserDisplayName(reporterNames, providerId);
        return (
          <Typography variant="body2" color="text.secondary">
            {displayName || (providerId ? 'Unknown user' : 'Anonymous')}
          </Typography>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      renderCell: params => {
        const issue = params.row as ScraperIssue;
        const isNew = issue.state === 'NEW';

        return (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {isNew && (
              <>
                <Tooltip title="Mark as Fixed">
                  <IconButton size="small" color="success" onClick={() => handleMarkFixed(issue)}>
                    <CheckIcon />
                  </IconButton>
                </Tooltip>
              </>
            )}
            <Tooltip title="Delete Issue">
              <IconButton size="small" color="error" onClick={() => handleDeleteClick(issue)}>
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Scraper Issues</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>State Filter</InputLabel>
            <Select
              value={stateFilter}
              label="State Filter"
              onChange={e => setStateFilter(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="NEW">New</MenuItem>
              <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
              <MenuItem value="FIXED">Fixed</MenuItem>
              <MenuItem value="WONT_FIX">Won't Fix</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title="Refresh">
            <IconButton onClick={() => refetch()}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Issues reported by analysts (extension) or detected by the scraper when selectors don&apos;t
        retrieve the required data. Count shows how many times the same issue was encountered.
        {totalCount > 0 && ` Showing ${issues.length} of ${totalCount} issues.`}
      </Typography>

      <DataGrid
        rows={issues}
        columns={columns}
        loading={isLoading}
        paginationMode="server"
        rowCount={totalCount}
        pageSizeOptions={[25, 50, 100]}
        paginationModel={{
          page: page - 1,
          pageSize: pageSize,
        }}
        onPaginationModelChange={model => {
          setPage(model.page + 1);
          setPageSize(model.pageSize);
        }}
        disableRowSelectionOnClick
        autoHeight
        sx={{ minHeight: 400 }}
        initialState={{
          sorting: {
            sortModel: [{ field: 'created_at', sort: 'asc' }],
          },
        }}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Issue</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this issue report?</Typography>
          {issueToDelete && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>URL:</strong> {issueToDelete.url}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Comment:</strong> {issueToDelete.comment}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Issues;
